// Big enough to judge an item's condition, small enough to email five of them.
const EMAIL_PHOTO_MAX_DIMENSION = 2048
const EMAIL_PHOTO_JPEG_QUALITY = 0.85

/** The largest size with the same shape whose longer side is at most `max`. Never enlarges. */
export function fitWithin(width, height, max) {
  const scale = Math.min(1, max / Math.max(width, height))
  return { width: Math.round(width * scale), height: Math.round(height * scale) }
}

/**
 * Re-save a photo as a JPEG no larger than 2048 px on its longer side, for
 * sending by email. HEIC is converted first. Throws if the file is not an
 * image the browser can read.
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function shrinkPhotoForEmail(file) {
  let url = null
  try {
    // Loaded on first use: imageProcessing pulls in the HEIC converter, which
    // needs a Web Worker and should not ride along with every deal modal.
    const { convertHeicToJpeg, loadImage } = await import('./imageProcessing')
    const source = await convertHeicToJpeg(file)
    url = URL.createObjectURL(source)
    const image = await loadImage(url)
    const { width, height } = fitWithin(image.naturalWidth, image.naturalHeight, EMAIL_PHOTO_MAX_DIMENSION)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#FFFFFF' // a transparent PNG would turn black as a JPEG
    ctx.fillRect(0, 0, width, height)
    ctx.drawImage(image, 0, 0, width, height)

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', EMAIL_PHOTO_JPEG_QUALITY))
    if (!blob) throw new Error('Failed to encode image')
    return new File([blob], `${source.name.replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg' })
  } catch (error) {
    console.error('Photo shrink failed:', error)
    throw new Error(`"${file.name}" could not be read as a photo.`, { cause: error })
  } finally {
    if (url) URL.revokeObjectURL(url)
  }
}
